import { useEffect, useState } from "react";
import { Users, Briefcase, Plus, Trash2, Loader2 } from "lucide-react";

const BASE = import.meta.env.BASE_URL;

function adminHeaders(json = false): HeadersInit {
  const token = localStorage.getItem("admin_token") || "";
  const h: Record<string, string> = { "x-admin-token": token };
  if (json) h["Content-Type"] = "application/json";
  return h;
}

type Banker = { id: number; name: string; email: string; title?: string };
type Investor = { id: number; name: string; email: string };
type Cohort = { id: number; slug: string; name: string };

export default function CohortMembers({ cohortSlug }: { cohortSlug: string }) {
  const [cohort, setCohort] = useState<Cohort | null>(null);
  const [bankers, setBankers] = useState<Banker[]>([]);
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [allBankers, setAllBankers] = useState<Banker[]>([]);
  const [allInvestors, setAllInvestors] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [pickBankerId, setPickBankerId] = useState<string>("");
  const [pickInvestorId, setPickInvestorId] = useState<string>("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const cohortsRes = await fetch(`${BASE}api/cohorts`, { headers: adminHeaders() });
      const cohortsArr: Cohort[] = cohortsRes.ok ? await cohortsRes.json() : [];
      const c = cohortsArr.find(x => x.slug === cohortSlug) || null;
      setCohort(c);
      if (!c) { setLoading(false); return; }

      const [b, i, ab, ai] = await Promise.all([
        fetch(`${BASE}api/cohorts/${c.id}/bankers`, { headers: adminHeaders() }).then(r => r.ok ? r.json() : { bankers: [] }),
        fetch(`${BASE}api/cohorts/${c.id}/investors`, { headers: adminHeaders() }).then(r => r.ok ? r.json() : { investors: [] }),
        fetch(`${BASE}api/admin/bankers`, { headers: adminHeaders() }).then(r => r.ok ? r.json() : []),
        fetch(`${BASE}api/investors`, { headers: adminHeaders() }).then(r => r.ok ? r.json() : []),
      ]);
      setBankers(b.bankers || []);
      setInvestors(i.investors || []);
      setAllBankers(Array.isArray(ab) ? ab : []);
      setAllInvestors(Array.isArray(ai) ? ai : []);
    } catch (e: any) {
      setError(e?.message || "Failed to load");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cohortSlug]);

  const addBanker = async () => {
    if (!cohort || !pickBankerId) return;
    setBusy("add-banker");
    try {
      const r = await fetch(`${BASE}api/cohorts/${cohort.id}/bankers`, {
        method: "POST",
        headers: adminHeaders(true),
        body: JSON.stringify({ bankerId: Number(pickBankerId) }),
      });
      if (!r.ok) throw new Error((await r.json()).error || "Add failed");
      setPickBankerId("");
      await load();
    } catch (e: any) { setError(e.message); }
    setBusy(null);
  };

  const removeBanker = async (bankerId: number) => {
    if (!cohort || !confirm("Remove this banker from the cohort?")) return;
    setBusy(`rm-b-${bankerId}`);
    try {
      const r = await fetch(`${BASE}api/cohorts/${cohort.id}/bankers/${bankerId}`, { method: "DELETE", headers: adminHeaders() });
      if (!r.ok) throw new Error("Remove failed");
      await load();
    } catch (e: any) { setError(e.message); }
    setBusy(null);
  };

  const addInvestor = async () => {
    if (!cohort || !pickInvestorId) return;
    setBusy("add-investor");
    try {
      const r = await fetch(`${BASE}api/cohorts/${cohort.id}/investors`, {
        method: "POST",
        headers: adminHeaders(true),
        body: JSON.stringify({ investorId: Number(pickInvestorId) }),
      });
      if (!r.ok) throw new Error((await r.json()).error || "Add failed");
      setPickInvestorId("");
      await load();
    } catch (e: any) { setError(e.message); }
    setBusy(null);
  };

  const removeInvestor = async (investorId: number) => {
    if (!cohort || !confirm("Remove this investor from the cohort?")) return;
    setBusy(`rm-i-${investorId}`);
    try {
      const r = await fetch(`${BASE}api/cohorts/${cohort.id}/investors/${investorId}`, { method: "DELETE", headers: adminHeaders() });
      if (!r.ok) throw new Error("Remove failed");
      await load();
    } catch (e: any) { setError(e.message); }
    setBusy(null);
  };

  if (loading) return <div className="p-8 text-sm text-muted-foreground">Loading members…</div>;
  if (!cohort) return <div className="p-8 text-sm text-destructive">Cohort not found.</div>;

  const bankerIds = new Set(bankers.map(b => b.id));
  const investorIds = new Set(investors.map(i => i.id));
  const availableBankers = allBankers.filter(b => !bankerIds.has(b.id));
  const availableInvestors = allInvestors.filter(i => !investorIds.has(i.id));

  return (
    <div className="space-y-8" data-testid="cohort-members-page">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Cohort Members</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage which bankers and investors belong to <span className="font-medium">{cohort.name}</span>.
        </p>
      </header>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 text-destructive text-sm px-3 py-2">
          {error}
        </div>
      )}

      <section className="rounded-lg border border-border bg-card">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-primary" />
          <h2 className="font-semibold">Bankers</h2>
          <span className="ml-auto text-xs text-muted-foreground">{bankers.length} member{bankers.length === 1 ? "" : "s"}</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2">
            <select
              value={pickBankerId}
              onChange={(e) => setPickBankerId(e.target.value)}
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
              data-testid="select-add-banker"
            >
              <option value="">Add a banker…</option>
              {availableBankers.map(b => (
                <option key={b.id} value={b.id}>{b.name} — {b.email}</option>
              ))}
            </select>
            <button
              onClick={addBanker}
              disabled={!pickBankerId || busy === "add-banker"}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-1 disabled:opacity-50"
              data-testid="button-add-banker"
            >
              {busy === "add-banker" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </button>
          </div>
          {bankers.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No bankers in this cohort yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {bankers.map(b => (
                <li key={b.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium">{b.name}</p>
                    <p className="text-xs text-muted-foreground">{b.title ? `${b.title} · ` : ""}{b.email}</p>
                  </div>
                  <button
                    onClick={() => removeBanker(b.id)}
                    disabled={busy === `rm-b-${b.id}`}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    data-testid={`button-remove-banker-${b.id}`}
                  >
                    {busy === `rm-b-${b.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="rounded-lg border border-border bg-card">
        <div className="px-5 py-4 border-b border-border flex items-center gap-2">
          <Users className="w-4 h-4 text-primary" />
          <h2 className="font-semibold">Investors</h2>
          <span className="ml-auto text-xs text-muted-foreground">{investors.length} member{investors.length === 1 ? "" : "s"}</span>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2">
            <select
              value={pickInvestorId}
              onChange={(e) => setPickInvestorId(e.target.value)}
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm"
              data-testid="select-add-investor"
            >
              <option value="">Add an investor…</option>
              {availableInvestors.map(i => (
                <option key={i.id} value={i.id}>{i.name} — {i.email}</option>
              ))}
            </select>
            <button
              onClick={addInvestor}
              disabled={!pickInvestorId || busy === "add-investor"}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium flex items-center gap-1 disabled:opacity-50"
              data-testid="button-add-investor"
            >
              {busy === "add-investor" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Add
            </button>
          </div>
          {investors.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">No investors in this cohort yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {investors.map(i => (
                <li key={i.id} className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium">{i.name}</p>
                    <p className="text-xs text-muted-foreground">{i.email}</p>
                  </div>
                  <button
                    onClick={() => removeInvestor(i.id)}
                    disabled={busy === `rm-i-${i.id}`}
                    className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    data-testid={`button-remove-investor-${i.id}`}
                  >
                    {busy === `rm-i-${i.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
